import {AudioListener} from 'three'

import {AUDIO_SESSION_TYPE} from '../config'

/**
 * Owns the AudioContext and the policy for getting it out of `suspended`.
 *
 * Its own file because unlock policy is the part of audio most likely to change
 * out from under us: it is browser behaviour, not app behaviour. Keeping it -
 * and the reasoning below - in one place means there is exactly one file to
 * revisit when a mobile browser changes its mind.
 *
 * THE TAP THAT PLACES THE MODEL MAY NOT BE ABLE TO UNLOCK AUDIO. `pointerdown`
 * does not reliably carry user activation for touch input; the matching
 * `pointerup` / `touchend` does. Our placement trigger is `pointerdown`
 * (app-module.ts), so betting on it would mean silence on iOS.
 *
 * So: listeners go on `window` in the CAPTURE phase, across every event that
 * could plausibly carry activation, and NONE of them is removed until the
 * context is actually running. Removing the rest after the first attempt fails
 * is precisely the bug that leaves a session mute forever - `pointerdown` fires,
 * resume() rejects, the `pointerup` handler is torn down alongside it, and
 * nothing ever unlocks. Capture also means these run before the canvas handler,
 * so callers never have to think about ordering against its early returns.
 *
 * `AudioListener` is created but NOT added to the camera. A non-positional
 * `THREE.Audio` routes gain -> listener.gain -> destination and never touches a
 * PannerNode, so the listener's transform is meaningless; parenting it to the
 * SLAM camera would only buy nine `linearRampToValueAtTime` calls per frame,
 * driven by a three-internal timer that is not connected to the Page Visibility
 * API - reintroducing the exact backgrounding jump that `Timer.connect(document)`
 * exists to avoid.
 */

/**
 * Everything that might carry user activation, plus the one event that signals
 * a possible return from an iOS interruption (a call, Siri).
 */
const UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchend', 'click', 'visibilitychange'] as const

export interface AudioUnlock {
  /**
   * The DOM AudioContext.
   *
   * Read off the listener rather than `THREE.AudioContext.getContext()`:
   * @types/three types that static as three's own AudioContext class, which has
   * no `state`, `resume` or `decodeAudioData`. The instance is the same
   * module-level singleton either way.
   */
  readonly context: AudioContext
  readonly listener: AudioListener
  readonly isRunning: boolean
  /** Idempotent and synchronous. Safe to call from any event handler. */
  unlock(): void
  dispose(): void
}

export const createAudioUnlock = (signal?: AbortSignal): AudioUnlock => {
  const listener = new AudioListener()
  const context = listener.context

  // Experimental and Safari-only, so it is typed inline rather than in a .d.ts:
  // we touch exactly one property, and the ambient-declaration policy in
  // types/xr8.d.ts is to declare only what is actually called.
  const session = (navigator as Navigator & {audioSession?: {type: string}}).audioSession
  if (session !== undefined && AUDIO_SESSION_TYPE !== 'auto') session.type = AUDIO_SESSION_TYPE

  const unlock = (): void => {
    // `!== 'running'` rather than `=== 'suspended'` so iOS's own
    // `'interrupted'` state - absent from the DOM union, hence no cast needed -
    // is covered too.
    if (context.state === 'running' || context.state === 'closed') return
    // The rejection is deliberately swallowed. It means "that event carried no
    // activation", which is an expected outcome on most taps and not worth a
    // console line each time. The next event retries.
    void context.resume().catch(() => {})
  }

  const options = signal
    ? ({passive: true, capture: true, signal} as const)
    : ({passive: true, capture: true} as const)
  for (const event of UNLOCK_EVENTS) window.addEventListener(event, unlock, options)

  return {
    context,
    listener,
    get isRunning() {
      return context.state === 'running'
    },
    unlock,
    dispose: () => {
      // Harmless if the AbortSignal already removed them.
      for (const event of UNLOCK_EVENTS) {
        window.removeEventListener(event, unlock, {capture: true})
      }
      // Deliberately NOT context.close(). THREE.AudioContext memoises the
      // context module-globally, so closing it would kill every later
      // AudioListener on the page - and onDetach can fire while the page lives on.
    },
  }
}
